import { createTheme, responsiveFontSizes } from '@mui/material/styles';
import { designTokens } from './designTokens';

const getTheme = (mode) => {
  const isDark = mode === 'dark';

  const theme = createTheme({
    palette: {
      mode,
      primary: {
        main: designTokens?.colors?.primary?.[600] || '#1976d2',
        light: designTokens?.colors?.primary?.[400] || '#42a5f5',
        dark: designTokens?.colors?.primary?.[800] || '#1565c0',
        contrastText: '#ffffff',
      },
      secondary: {
        main: designTokens?.colors?.secondary?.[600] || '#9c27b0',
        light: designTokens?.colors?.secondary?.[400] || '#ba68c8',
        dark: designTokens?.colors?.secondary?.[800] || '#7b1fa2',
        contrastText: '#ffffff',
      },
      error: {
        main: designTokens?.colors?.error?.main || '#d32f2f',
        light: designTokens?.colors?.error?.light || '#ef5350',
        dark: designTokens?.colors?.error?.dark || '#c62828',
      },
      warning: {
        main: designTokens?.colors?.warning?.main || '#ed6c02',
        light: designTokens?.colors?.warning?.light || '#ff9800',
        dark: designTokens?.colors?.warning?.dark || '#e65100',
      },
      info: {
        main: designTokens?.colors?.info?.main || '#0288d1',
        light: designTokens?.colors?.info?.light || '#03a9f4',
        dark: designTokens?.colors?.info?.dark || '#01579b',
      },
      success: {
        main: designTokens?.colors?.success?.main || '#2e7d32',
        light: designTokens?.colors?.success?.light || '#4caf50',
        dark: designTokens?.colors?.success?.dark || '#1b5e20',
      },
      background: {
        default: isDark
          ? designTokens?.colors?.neutral?.[950] || '#121212'
          : designTokens?.colors?.background?.default || '#ffffff',
        paper: isDark
          ? designTokens?.colors?.neutral?.[900] || '#1e1e1e'
          : designTokens?.colors?.background?.paper || '#f5f5f5',
      },
      text: {
        primary: isDark
          ? designTokens?.colors?.neutral?.[100] || '#ffffff'
          : designTokens?.colors?.text?.primary || '#000000',
        secondary: isDark
          ? designTokens?.colors?.neutral?.[400] || 'rgba(255,255,255,0.7)'
          : designTokens?.colors?.text?.secondary || 'rgba(0,0,0,0.6)',
      },
      grey: designTokens?.colors?.neutral,
    },
    typography: {
      fontFamily: designTokens?.typography?.fontFamily?.primary,
      h1: {
        fontFamily: designTokens?.typography?.fontFamily?.secondary,
        fontSize: designTokens?.typography?.fontSize?.['6xl'],
        fontWeight: designTokens?.typography?.fontWeight?.extrabold,
        lineHeight: designTokens?.typography?.lineHeight?.tight,
        letterSpacing: designTokens?.typography?.letterSpacing?.tight,
      },
      h2: {
        fontFamily: designTokens?.typography?.fontFamily?.secondary,
        fontSize: designTokens?.typography?.fontSize?.['5xl'],
        fontWeight: designTokens?.typography?.fontWeight?.bold,
        lineHeight: designTokens?.typography?.lineHeight?.tight,
        letterSpacing: designTokens?.typography?.letterSpacing?.tight,
      },
      h3: {
        fontFamily: designTokens?.typography?.fontFamily?.secondary,
        fontSize: designTokens?.typography?.fontSize?.['4xl'],
        fontWeight: designTokens?.typography?.fontWeight?.bold,
        lineHeight: designTokens?.typography?.lineHeight?.snug,
        letterSpacing: designTokens?.typography?.letterSpacing?.normal,
      },
      h4: {
        fontFamily: designTokens?.typography?.fontFamily?.secondary,
        fontSize: designTokens?.typography?.fontSize?.['3xl'],
        fontWeight: designTokens?.typography?.fontWeight?.semibold,
        lineHeight: designTokens?.typography?.lineHeight?.snug,
      },
      h5: {
        fontFamily: designTokens?.typography?.fontFamily?.secondary,
        fontSize: designTokens?.typography?.fontSize?.['2xl'],
        fontWeight: designTokens?.typography?.fontWeight?.semibold,
        lineHeight: designTokens?.typography?.lineHeight?.normal,
      },
      h6: {
        fontFamily: designTokens?.typography?.fontFamily?.secondary,
        fontSize: designTokens?.typography?.fontSize?.xl,
        fontWeight: designTokens?.typography?.fontWeight?.semibold,
        lineHeight: designTokens?.typography?.lineHeight?.normal,
      },
      body1: {
        fontSize: designTokens?.typography?.fontSize?.base,
        lineHeight: designTokens?.typography?.lineHeight?.relaxed,
      },
      body2: {
        fontSize: designTokens?.typography?.fontSize?.sm,
        lineHeight: designTokens?.typography?.lineHeight?.relaxed,
      },
      button: {
        fontWeight: designTokens?.typography?.fontWeight?.semibold,
        textTransform: 'none',
        letterSpacing: designTokens?.typography?.letterSpacing?.normal,
      },
    },
    shape: {
      borderRadius: parseInt(designTokens?.borderRadius?.lg || '8', 10),
    },
    // FIX: Must contain EXACTLY 25 shadow values (0 through 24)
    shadows: [
      'none', // 0
      designTokens?.shadows?.xs || 'none', // 1
      designTokens?.shadows?.sm || 'none', // 2
      designTokens?.shadows?.md || 'none', // 3
      designTokens?.shadows?.lg || 'none', // 4
      designTokens?.shadows?.xl || 'none', // 5
      designTokens?.shadows?.['2xl'] || 'none', // 6
      designTokens?.shadows?.['2xl'] || 'none', // 7
      designTokens?.shadows?.['2xl'] || 'none', // 8
      designTokens?.shadows?.['2xl'] || 'none', // 9
      designTokens?.shadows?.['2xl'] || 'none', // 10
      designTokens?.shadows?.['2xl'] || 'none', // 11
      designTokens?.shadows?.['2xl'] || 'none', // 12
      designTokens?.shadows?.['2xl'] || 'none', // 13
      designTokens?.shadows?.['2xl'] || 'none', // 14
      designTokens?.shadows?.['2xl'] || 'none', // 15
      designTokens?.shadows?.['2xl'] || 'none', // 16
      designTokens?.shadows?.['2xl'] || 'none', // 17
      designTokens?.shadows?.['2xl'] || 'none', // 18
      designTokens?.shadows?.['2xl'] || 'none', // 19
      designTokens?.shadows?.['2xl'] || 'none', // 20
      designTokens?.shadows?.['2xl'] || 'none', // 21
      designTokens?.shadows?.['2xl'] || 'none', // 22
      designTokens?.shadows?.['2xl'] || 'none', // 23
      designTokens?.shadows?.['2xl'] || 'none', // 24 (25th item added)
    ],
    transitions: {
      easing: {
        easeIn: designTokens?.transitions?.easing?.easeIn,
        easeOut: designTokens?.transitions?.easing?.easeOut,
        easeInOut: designTokens?.transitions?.easing?.easeInOut,
      },
      duration: {
        shortest: 150,
        shorter: 200,
        short: 250,
        standard: 300,
        complex: 375,
        enteringScreen: 225,
        leavingScreen: 195,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: () => ({
          body: {
            transition: `background-color ${designTokens?.transitions?.duration?.slow || '0.3s'} ${designTokens?.transitions?.easing?.easeInOut || 'ease-in-out'},
                         color ${designTokens?.transitions?.duration?.slow || '0.3s'} ${designTokens?.transitions?.easing?.easeInOut || 'ease-in-out'}`,
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          },
          '*': {
            scrollbarWidth: 'thin',
            scrollbarColor: `${designTokens?.colors?.primary?.[400] || '#42a5f5'} transparent`,
          },
          '::selection': {
            backgroundColor: designTokens?.colors?.primary?.[200] || '#90caf9',
            color: designTokens?.colors?.primary?.[900] || '#0d47a1',
          },
        }),
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: designTokens?.borderRadius?.lg,
            padding: `${designTokens?.spacing?.[2] || '8px'} ${designTokens?.spacing?.[4] || '16px'}`,
            fontWeight: designTokens?.typography?.fontWeight?.semibold,
            textTransform: 'none',
            boxShadow: 'none',
            transition: `all ${designTokens?.transitions?.duration?.normal || '0.2s'} ${designTokens?.transitions?.easing?.easeOut || 'ease-out'}`,
            '&:hover': {
              boxShadow: designTokens?.shadows?.colored?.primary,
              transform: 'translateY(-1px)',
            },
          },
          contained: {
            '&:hover': {
              boxShadow: designTokens?.shadows?.colored?.primary,
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: designTokens?.borderRadius?.xl,
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
            backdropFilter: 'blur(12px)',
            transition: `all ${designTokens?.transitions?.duration?.normal || '0.2s'} ${designTokens?.transitions?.easing?.easeOut || 'ease-out'}`,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: isDark ? designTokens?.shadows?.colored?.glow : designTokens?.shadows?.xl,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            borderRadius: designTokens?.borderRadius?.xl,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: designTokens?.borderRadius?.lg,
              transition: `all ${designTokens?.transitions?.duration?.normal || '0.2s'} ${designTokens?.transitions?.easing?.easeOut || 'ease-out'}`,
              '&:hover': {
                boxShadow: `0 0 0 3px ${designTokens?.colors?.primary?.[100] || '#bbdefb'}`,
              },
              '&.Mui-focused': {
                boxShadow: `0 0 0 3px ${designTokens?.colors?.primary?.[200] || '#90caf9'}`,
              },
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: designTokens?.borderRadius?.lg,
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            borderRadius: designTokens?.borderRadius?.lg,
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            borderRadius: designTokens?.borderRadius?.lg,
          },
        },
      },
      MuiFormControl: {
        styleOverrides: {
          root: {
            minWidth: 120,
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: '0.875rem',
            fontWeight: 500,
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            borderRadius: designTokens?.borderRadius?.lg,
          },
          select: {
            minWidth: 0,
            fontSize: '0.875rem',
            fontWeight: 600,
            lineHeight: 1.4,
            paddingTop: '10px',
            paddingBottom: '10px',
            paddingLeft: '14px',
            paddingRight: '36px !important',
            display: 'flex',
            alignItems: 'center',
          },
          icon: {
            right: '10px',
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            minWidth: '240px !important',
            maxWidth: 'calc(100vw - 24px) !important',
            borderRadius: designTokens?.borderRadius?.xl || '14px',
            border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(15, 23, 42, 0.1)'}`,
            boxShadow: isDark
              ? '0 16px 36px rgba(0, 0, 0, 0.6)'
              : '0 16px 36px rgba(15, 23, 42, 0.14)',
            backgroundImage: 'none',
          },
          list: {
            padding: '6px',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: '8px',
            margin: '2px 0',
            padding: '10px 14px',
            fontSize: '0.875rem',
            fontWeight: 500,
            lineHeight: 1.45,
            whiteSpace: 'normal !important',
            wordBreak: 'break-word',
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.05)',
            },
            '&.Mui-selected': {
              fontWeight: 700,
              backgroundColor: isDark ? 'rgba(37, 99, 235, 0.2)' : 'rgba(37, 99, 235, 0.1)',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(37, 99, 235, 0.28)' : 'rgba(37, 99, 235, 0.16)',
              },
            },
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            borderRadius: designTokens?.borderRadius?.md,
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            borderRadius: `${designTokens?.borderRadius?.xl} !important`,
            overflow: 'hidden',
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: designTokens?.borderRadius?.lg,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: designTokens?.borderRadius?.['2xl'],
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            borderRadius: designTokens?.borderRadius?.xl,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            background: isDark ? 'rgba(15, 23, 42, 0.8)' : 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(20px)',
            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
            boxShadow: 'none',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
            background: isDark ? designTokens?.colors?.neutral?.[900] : designTokens?.colors?.background?.paper,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: designTokens?.borderRadius?.full,
            fontWeight: designTokens?.typography?.fontWeight?.medium,
            transition: `all ${designTokens?.transitions?.duration?.normal || '0.2s'} ${designTokens?.transitions?.easing?.easeOut || 'ease-out'}`,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: designTokens?.borderRadius?.lg,
            transition: `all ${designTokens?.transitions?.duration?.normal || '0.2s'} ${designTokens?.transitions?.easing?.easeOut || 'ease-out'}`,
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
            },
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            borderRadius: designTokens?.borderRadius?.lg,
          },
        },
      },
    },
  });

  return theme;
};

export const createGoafreetTheme = (mode) => {
  const theme = getTheme(mode);
  return responsiveFontSizes(theme);
};

export default createGoafreetTheme;
